import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import * as endpoints from "@/api/endpoints";
import type { Order } from "@/api/types";
import { apiErrorMessage } from "@/api/client";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime, formatYen } from "@/utils/format";

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const justCreated = (location.state as { justCreated?: boolean } | null)?.justCreated;

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const orderId = Number(id);
    if (Number.isNaN(orderId)) {
      setError("不正な発注IDです");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await endpoints.fetchOrder(orderId);
        if (!cancelled) setOrder(res);
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e, "発注詳細の取得に失敗しました"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <div className="text-center py-12 text-gray-500">読み込み中...</div>;
  if (error)
    return (
      <div className="space-y-4">
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
        <Link to="/orders" className="btn-secondary inline-block">
          発注履歴へ戻る
        </Link>
      </div>
    );
  if (!order) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">発注詳細</h1>
          <p className="text-sm text-gray-500 mt-1">{order.order_number}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {justCreated && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          発注を受け付けました。確認メールをお送りしました。承認後に出荷手続きを開始します。
        </div>
      )}

      <div className="card p-4 grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="text-gray-500 text-xs">発注日時</div>
          <div>{formatDateTime(order.created_at)}</div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">合計金額 (税込)</div>
          <div className="text-lg font-bold">{formatYen(order.total_amount)}</div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">承認日時</div>
          <div>{formatDateTime(order.approved_at)}</div>
        </div>
        <div>
          <div className="text-gray-500 text-xs">出荷日時</div>
          <div>{formatDateTime(order.shipped_at)}</div>
        </div>
        <div className="sm:col-span-2">
          <div className="text-gray-500 text-xs">発送先</div>
          <div className="whitespace-pre-wrap">{order.shipping_address || "-"}</div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b font-semibold">明細</div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-600">JAN</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600">商品名</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600">単価 (税込)</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600">数量</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600">小計 (税込)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(order.items ?? []).map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 font-mono text-xs text-gray-500">{item.jan}</td>
                <td className="px-4 py-2">
                  {item.product_name ?? <span className="text-gray-400">（商品名不明）</span>}
                </td>
                <td className="px-4 py-2 text-right">{formatYen(item.tax_inclusive_unit_price)}</td>
                <td className="px-4 py-2 text-right">{item.quantity}</td>
                <td className="px-4 py-2 text-right font-medium">{formatYen(item.subtotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t">
            <tr>
              <td colSpan={4} className="px-4 py-2 text-right text-gray-600">
                合計 (税込)
              </td>
              <td className="px-4 py-2 text-right font-bold">{formatYen(order.total_amount)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Link to="/orders" className="btn-secondary inline-block">
        発注履歴へ戻る
      </Link>
    </div>
  );
}
